<script setup lang="ts">
import type { StaticFile } from "~/classes/file/StaticFile"

import checkBadge from "~/assets/icons/badge/check.svg"
import crossBadge from "~/assets/icons/badge/cross.svg"

// TODO: allow selecting the items in the chat, versus just clicking to navigate to them

const props = defineProps({
  label: null,
  files: null
})

const { navigateToFile } = useNavigation()
const collapsed = ref(true)

// We only visualize the first 8 items, then we need to reverse them since the render order is
// last to first

const anyErrors = computed(() => props.files.some(([, , error]) => !!error))
const anyLoading = computed(() => props.files.some(([, loading]) => loading))

const onClickStack = () => {
  if (props.files.length === 1)
    return // don't need to toggle if there's only one file
  collapsed.value = !collapsed.value
}

const goToFile = (file: StaticFile) => {
  if (collapsed.value)
    return // don't navigate if we're collapsed
  navigateToFile(file)
}
</script>

<template>
  <div class="actions">
    <div
      :class="{ collapsed }"
      class="stack"
      @click="onClickStack"
    >
      <div
        v-for="[file, loading, error] in files.slice(0, 8)"
        :key="file.id"
        class="file"
        @click="goToFile(file)"
      >
        <file-thumbnail
          :file
          :container-width="collapsed ? 100 : 60"
          :container-height="collapsed ? 100 : 60"
          shadowed
          hide-stale
          class="file-thumbnail"
        />
        <div v-if="loading" />
        <cross-badge v-else-if="error" class="cross status" />
        <check-badge v-else class="check status" />
      </div>
    </div>
    <dropdown
      anchor-x="left"
      anchor-y="bottom"
      flow-x="right"
      flow-y="down"
    >
      <template #default="{ toggle, active }">
        <div
          class="label"
          :class="{ active }"
          @click="toggle()"
        >
          <interface-spinner
            v-if="anyLoading"
            dots
            class="status loading"
          />
          <icon-action
            v-else-if="anyErrors"
            size="xs"
            type="cross"
            class="cross status"
          />
          <icon-action
            v-else
            size="xs"
            type="check"
            class="check status"
          />
          <icon-action
            size="xs"
            type="chevron-down"
            class="chevron"
          />
          <span>{{ label }}</span>
        </div>
      </template>
      <template #menu>
        <dropdown-menu class="file-reads-menu">
          <menu-section>
            <div
              v-for="[file, loading, error] in files"
              :key="file.id"
              class="file-reads-menu-result"
            >
              <file-inline
                :file
                class="file-reads-menu-inline"
                navigable
                location="agent"
              />
              <interface-spinner
                v-if="loading"
                dots
                class="status loading"
              />
              <icon-action
                v-else-if="error"
                type="cross"
                size="xs"
                class="cross status"
              />
              <icon-action
                v-else
                type="check"
                size="xs"
                class="check status"
              />
            </div>
          </menu-section>
        </dropdown-menu>
      </template>
    </dropdown>
  </div>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'

.actions
  @apply space-y-2

.file
  @apply transition transition-all transition-duration-100

.stack
  @apply cursor-pointer

.stack.collapsed
  @apply flex relative w-27 h-27 p-1 items-center justify-center
  .file
    @apply absolute flex items-center justify-center
    &:nth-last-child(1)
      @apply z-8
      @apply rotate-[0deg] shadow-black/40 shadow shadow-lg
    &:nth-last-child(2)
      @apply z-7
      @apply -rotate-[6deg] shadow-black/10 shadow shadow-lg
    &:nth-last-child(3)
      @apply z-6
      @apply rotate-[10deg] shadow-black/10 shadow shadow-lg
    &:nth-last-child(4)
      @apply z-5
      @apply -rotate-[8deg] shadow-black/10 shadow shadow-lg
    &:nth-last-child(5)
      @apply z-4
      @apply rotate-[2deg] shadow-black/10 shadow shadow-lg
    &:nth-last-child(6)
      @apply z-3
    &:nth-last-child(7)
      @apply z-2
    &:nth-last-child(8)
      @apply z-1
  .status
    @apply hidden

.stack:not(.collapsed)
  @apply grid grid-cols-4 p-1
  .file
    @apply mx-auto relative
  .status
    @apply absolute -bottom-1.5 -right-1.5
    filter: drop-shadow(0 0px 4px rgba(0, 0, 0, 0.8))

.file-thumbnail
  @apply max-h-full max-w-full

.label
  @apply t-panel-sm inline-flex items-center gap-0.75 text-[#{$global-t-secondary}]
  @apply -mx-1.5 pl-[4px] pr-[8px] py-[2px] rounded-full
  .status
    @apply block
  .chevron
    @apply hidden
  &:hover, &.active
    @apply bg-$global-steel-secondary text-[#{$global-t-default}] cursor-pointer
    .chevron
      @apply block text-[#{$global-t-secondary}]
    .status
      @apply hidden

.row
  @apply flex items-center gap-[#{$panel-agent-gap-step-item}]
  @apply px-[#{$panel-agent-px-step-item}] py-[3px]
  @apply t-panel-xs

.more
  @apply flex-none
  @apply bg-$global-steel-accent
  @apply text-[#{$global-t-secondary}]
  @apply p-[3px] rounded-1

.status
  @apply flex-none flex items-center

.check
  @apply text-$global-green-primary
.cross
  @apply text-$global-red-primary

.spinner
  @apply w-3 h-3 flex items-center justify-center
.inner
  @apply w-2 h-2 text-$global-t-secondary

.file-reads-menu
  @apply mt-1 -ml-1
  @apply min-w-40
  .file-reads-menu-result
    @apply flex items-center
  .file-reads-menu-inline
    @apply text-ellipses-inline w-full
  .status
    @apply flex-none
</style>
